// Color, label, and ordering tables for event categories.
//
// These tables are derived at module-init time from `app.config.ts`. The
// frontend reads from CATEGORY_COLOR / CATEGORY_LABEL / CATEGORY_ORDER
// everywhere a category id needs a hex, a human-readable label, or a
// canonical sort order.
//
// Apps choose their own palette by editing `app.config.ts.taxonomy.categories`;
// no code changes required here.
import { config } from './config';
import type { Confidence, EventStatus } from './events';

const FALLBACK_COLOR = '#a8a29e';

/** Hex color per category id, derived from `app.config.ts.taxonomy.categories`. */
export const CATEGORY_COLOR: Record<string, string> = Object.fromEntries(
  config.taxonomy.categories.map((c) => [c.id, c.color]),
);

/** Human-readable label per category id. */
export const CATEGORY_LABEL: Record<string, { name: string; abbreviation: string; secondary: string }> = Object.fromEntries(
  config.taxonomy.categories.map((c) => [
    c.id,
    {
      name: c.label,
      // Abbreviation default: first 4 uppercased chars of id with non-alphanumerics stripped.
      abbreviation: c.id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || c.id.toUpperCase(),
      secondary: '',
    },
  ]),
);

/** Canonical display order — preserves the order categories are declared in config. */
export const CATEGORY_ORDER: string[] = config.taxonomy.categories.map((c) => c.id);

/**
 * Resolve a hex color for a category id, falling back to a neutral grey
 * when the id isn't declared in `app.config.ts`.
 */
export function getCategoryColor(id: string): string {
  return CATEGORY_COLOR[id] ?? FALLBACK_COLOR;
}

/** Labels for the optional secondary classification, derived from config.
 *  Accepts both legacy `string[]` and current `{id, label}[]` shapes —
 *  the runtime `typeof` check keeps both apps' configs working without
 *  forcing every consumer to converge on a single shape. */
export const SYNDROME_LABEL: Record<string, string> = (() => {
  const values = config.taxonomy.secondaryValues as
    | readonly (string | { id: string; label: string })[]
    | undefined;
  if (!values || values.length === 0) return {};
  return Object.fromEntries(
    values.map((v) => (typeof v === 'string' ? [v, v] : [v.id, v.label])),
  );
})();

export const STATUS_LABEL: Record<EventStatus, string> = {
  active: 'active',
  concluded: 'concluded',
  historical: 'historical',
};

export const CONFIDENCE_CLASSES: Record<Confidence, string> = {
  verified:    'bg-[var(--color-verified-soft)]    text-[var(--color-verified)]    ring-1 ring-[var(--color-verified)]/15',
  preliminary: 'bg-[var(--color-preliminary-soft)] text-[var(--color-preliminary)] ring-1 ring-[var(--color-preliminary)]/15',
  unverified:  'bg-[var(--color-unverified-soft)]  text-[var(--color-unverified)]  ring-1 ring-[var(--color-unverified)]/15',
};

/**
 * Marker radius in px from a primary-count value. Smaller, more precise:
 * sqrt scale, clamped to [4, 18]. Bigger circles are noise on a global view;
 * halo + selected states do the work of drawing attention instead.
 */
export function markerRadius(totalCases: number): number {
  if (totalCases <= 0) return 4;
  const r = 4 + Math.sqrt(totalCases) * 1.05;
  return Math.max(4, Math.min(18, r));
}
